import {
  applyDeskChromeDataset,
  deskChromeFromUnknown,
  hydrateDeskChrome,
  type DeskChrome,
} from "@northstar/dispatch-core";
import { COMPANION_CHROME_KEY } from "./companion";

function companyIdOf(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const company = (payload as { company?: { id?: unknown } }).company;
  return typeof company?.id === "string" ? company.id.trim() : "";
}

/**
 * Desk chrome for this phone.
 * A company snapshot's deskChrome wins over a stale payload.theme / company.theme.
 * An account career saved later, for the same company, wins over that snapshot —
 * Save career can publish night after the board snapshot still says day.
 * A cheap /v1/revision ping is not a source: its theme field prefers payload.theme
 * and was repainting Day every second.
 */
export function companionChromeFromNanode(input: {
  snapshot?: { exportedAtUtc?: string; payload?: unknown } | null;
  career?: { savedAtUtc?: string; payload?: unknown } | null;
}): DeskChrome {
  const snapPayload = input.snapshot?.payload;
  const careerPayload = input.career?.payload;
  const snapAt = input.snapshot?.exportedAtUtc?.trim() ?? "";
  const careerAt = input.career?.savedAtUtc?.trim() ?? "";
  const snapCo = companyIdOf(snapPayload);
  const careerCo = companyIdOf(careerPayload);
  const sameCompany = !snapCo || !careerCo || snapCo === careerCo;
  if (careerPayload && sameCompany && careerAt && (!snapAt || careerAt > snapAt)) {
    return deskChromeFromUnknown(careerPayload);
  }
  if (snapPayload) return deskChromeFromUnknown(snapPayload);
  if (careerPayload) return deskChromeFromUnknown(careerPayload);
  return hydrateDeskChrome(null);
}

/** Phone opens on Ops Classic Day. A saved desk theme still replaces this. */
export const COMPANION_DEFAULT_CHROME: DeskChrome = {
  theme: "light",
  themePack: "ops-classic",
  themeAccent: "#4a90c8",
};

export function loadCompanionChrome(storage?: Pick<Storage, "getItem"> | null): DeskChrome {
  try {
    const raw = storage?.getItem(COMPANION_CHROME_KEY);
    if (!raw) return { ...COMPANION_DEFAULT_CHROME };
    return hydrateDeskChrome(JSON.parse(raw) as Partial<DeskChrome>);
  } catch {
    return { ...COMPANION_DEFAULT_CHROME };
  }
}

export function saveCompanionChrome(
  chrome: Partial<{ theme: unknown; themePack: unknown; themeAccent: unknown }>,
  storage?: Pick<Storage, "setItem"> | null,
): DeskChrome {
  const next = hydrateDeskChrome({ ...loadCompanionChrome(storage as Pick<Storage, "getItem"> | null), ...chrome });
  storage?.setItem(COMPANION_CHROME_KEY, JSON.stringify(next));
  return next;
}

export function applyCompanionChrome(
  target: {
    dataset: DOMStringMap;
    style?: { setProperty(name: string, value: string): void; colorScheme?: string };
    ownerDocument?: { querySelector?(selector: string): { setAttribute(name: string, value: string): void } | null } | null;
  },
  chrome: Partial<DeskChrome> | null | undefined,
): DeskChrome {
  const next = applyDeskChromeDataset(target, chrome);
  if (target.style) target.style.colorScheme = next.theme === "light" ? "light" : "dark";
  const meta = target.ownerDocument?.querySelector?.('meta[name="theme-color"]');
  meta?.setAttribute("content", companionThemeColor(target, next));
  return next;
}

/** Status-bar color. Prefer the live token; the fallback matches Ops Classic --chrome. */
function companionThemeColor(target: object, chrome: DeskChrome): string {
  const node = target as { nodeType?: number };
  if (typeof getComputedStyle === "function" && node.nodeType === 1) {
    const css = getComputedStyle(target as Element);
    const token = css.getPropertyValue("--ns-topbar-bg").trim() || css.getPropertyValue("--chrome").trim();
    if (token) return token;
  }
  if (chrome.themePack === "ops-classic") return chrome.theme === "light" ? "#215085" : "#1f4c80";
  return chrome.theme === "light" ? "#e6ebf0" : "#10141b";
}

/** Clear phone chrome to defaults — used on sign-out so a friend does not inherit the last desk theme. */
export function resetCompanionChrome(
  storage?: (Pick<Storage, "setItem"> & Partial<Pick<Storage, "removeItem">>) | null,
): DeskChrome {
  const next = { ...COMPANION_DEFAULT_CHROME };
  storage?.removeItem?.(COMPANION_CHROME_KEY);
  storage?.setItem(COMPANION_CHROME_KEY, JSON.stringify(next));
  return next;
}

/** Desk snapshot chrome wins. Local chrome is only a cache of the last desk pull. */
export function applyChromeFromDeskPayload(
  payload: unknown,
  storage?: Pick<Storage, "setItem"> | null,
): DeskChrome {
  const chrome = deskChromeFromUnknown(payload);
  storage?.setItem(COMPANION_CHROME_KEY, JSON.stringify(chrome));
  return chrome;
}

/** Apply theme/accent from GET /v1/revision when the Nanode included those fields. */
export function applyChromeFromRevision(
  revision: { theme?: string; themePack?: string; themeAccent?: string },
  storage?: Pick<Storage, "setItem"> | null,
): DeskChrome | null {
  if (!revision.themePack?.trim() && !revision.themeAccent?.trim()) return null;
  return applyChromeFromDeskPayload(
    {
      theme: revision.theme,
      themePack: revision.themePack,
      themeAccent: revision.themeAccent,
    },
    storage,
  );
}
